import { useTranslations } from "next-intl";
import { useFeatureFlagVariantKey } from "posthog-js/react";
import { useLocalStorage } from "react-use";
import type { z } from "zod";

import {
  IconArrowRight16Line,
  IconXmark16Line,
} from "@/components/icons/iconoir";
import type { bannerSchema } from "@/components/nav/types";
import { Link } from "@/components/ui/link";
import { lg, useMediaMinWidth } from "@/hooks/use-media-min-width";
import { cn } from "@/lib/utils";

export function Banner({
  showBanner,
  bannerHeight,
  onBannerDismissed,
  text,
  linkText,
  linkUrl,
  external,
}: {
  showBanner: boolean;
  bannerHeight: number;
  onBannerDismissed: () => void;
} & z.infer<typeof bannerSchema>) {
  const t = useTranslations("Banner");
  return (
    <div
      role="alert"
      aria-live="polite"
      className={cn(
        "fixed inset-x-0 top-0 z-50 hidden bg-purple-300 transition-all ease-in-out lg:flex",
        showBanner ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0"
      )}
      style={{ height: `${bannerHeight}px` }}
    >
      <div className="relative container mx-auto w-full">
        <div className="flex h-full items-center justify-center gap-x-2 text-sm sm:text-base">
          <p className="text-body-sm text-purple-900">{text}</p>
          <Link
            href={linkUrl}
            external={Boolean(external)}
            className="group text-body-sm flex items-center gap-x-1 font-semibold text-purple-900 transition-colors"
          >
            {linkText}
            <IconArrowRight16Line className="stroke-purple-900 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
        <button
          className="hover:text-grey-200 absolute top-1/2 right-8 -translate-y-1/2 text-white transition-colors"
          aria-label={t("closeAnnouncement")}
          onClick={onBannerDismissed}
        >
          <IconXmark16Line className="stroke-purple-900" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
export function useShowBanner({
  enabled = true,
  bannerId,
  featureFlag,
}: {
  enabled?: boolean;
  bannerId: string;
  featureFlag?: string | null;
}) {
  const [bannerDismissed, setBannerDismissed] = useLocalStorage(
    `banner-dismissed-${bannerId}`,
    false
  );

  const showBannerFromFeatureFlag = useBannerFeatureFlag({
    // empty string needed but seems to be a valid feature flag key when passed to PostHog
    featureFlag: featureFlag ?? "",
  });

  const bannerHeight = 40; // Height of the banner in pixels
  const isDesktop = useMediaMinWidth(lg, false);

  return {
    showBanner:
      enabled && showBannerFromFeatureFlag
        ? isDesktop && !bannerDismissed
        : false,
    bannerHeight,
    onBannerDismissed: () => setBannerDismissed(true),
  };
}

export function useBannerFeatureFlag({ featureFlag }: { featureFlag: string }) {
  const variantKey = useFeatureFlagVariantKey(featureFlag);
  return (
    variantKey === true || (variantKey !== false && variantKey !== "control")
  );
}
